import { useMutation } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { toast } from "sonner";

/**
 * Hook for time entry approval and rejection actions.
 * Provides mutations for bulk approve and single reject.
 */
export function useTimeEntryActions(onSuccess?: () => void) {
  const trpc = useTRPC();

  const approveMutation = useMutation(
    trpc.timeEntry.bulkApproveTimeEntries.mutationOptions({
      onSuccess: (data) => {
        toast.success(
          `Successfully approved ${data.approved} time ${data.approved === 1 ? "entry" : "entries"}`,
        );
        onSuccess?.();
      },
      onError: (error) => {
        toast.error(error.message || "Failed to approve time entries");
      },
    }),
  );

  const rejectMutation = useMutation(
    trpc.timeEntry.rejectTimeEntry.mutationOptions({
      onSuccess: () => {
        toast.success(
          "Time entry rejected. The provider will be notified and can resubmit.",
        );
        onSuccess?.();
      },
      onError: (error) => {
        toast.error(error.message || "Failed to reject time entry");
      },
    }),
  );

  const resolveProviderIssueMutation = useMutation(
    trpc.timeEntry.resolveProviderReportedShiftIssue.mutationOptions({
      onSuccess: () => {
        toast.success("Provider-reported issue marked as resolved.");
        onSuccess?.();
      },
      onError: (error) => {
        toast.error(error.message || "Failed to resolve the reported issue");
      },
    }),
  );

  return {
    approveTimeEntries: approveMutation.mutateAsync,
    rejectTimeEntry: rejectMutation.mutateAsync,
    resolveProviderIssue: resolveProviderIssueMutation.mutateAsync,
    isApproving: approveMutation.isPending,
    isRejecting: rejectMutation.isPending,
    resolvingShiftId: resolveProviderIssueMutation.isPending
      ? resolveProviderIssueMutation.variables?.shiftId
      : undefined,
  };
}
